import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Stack,
  Alert,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Divider,
  InputAdornment,
} from "@mui/material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useHoldsApi, useStagesApi, useUser, useTeam } from "@/context/AppContext";
import type { Stage, HoldCreateUpdate, HoldMultiDayResult } from "api-client/models";
import { apiDateToDateOnly, createDateUTC, parseDateOnlyToLocalDate } from '@/utils/dateUtils';
import { readHoldCreationError } from '@/bookings/utils/holdCreation';
import { HoldCreationResult } from './HoldCreationResult';
import { Breadcrumbs } from "@/utils/components";
import { getSelectedOptionId } from '@/utils/selectionDefaults';

interface HoldFormData {
  title: string;
  stage: string;
  holdDate: Date | null;
  endDate: Date | null; // For multi-day holds
  isMultiDay: boolean;
  notes: string;

  // Operational fields - Timing
  doorsTime: Date | null;
  showTime: Date | null;
  endTime: Date | null;

  // Operational fields - Attendance
  attendance: number | null;
  showCapacity: number | null;

  // Operational fields - Revenue
  ticketRevenue: string;
  barRevenue: string;
  otherRevenue: string;

  // Operational fields - Expenses & Settlement
  totalExpenses: string;
  isSettled: boolean;
  settlementDate: Date | null;
  settlementNotes: string;
}

const initialFormData: HoldFormData = {
  title: "",
  stage: "",
  holdDate: null,
  endDate: null,
  isMultiDay: false,
  notes: "",

  // Operational fields - Timing
  doorsTime: null,
  showTime: null,
  endTime: null,

  // Operational fields - Attendance
  attendance: null,
  showCapacity: null,

  // Operational fields - Revenue
  ticketRevenue: "",
  barRevenue: "",
  otherRevenue: "",

  // Operational fields - Expenses & Settlement
  totalExpenses: "",
  isSettled: false,
  settlementDate: null,
  settlementNotes: "",
};

const HoldForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const holdsApi = useHoldsApi();
  const stagesApi = useStagesApi();
  const user = useUser();
  const team = useTeam();
  const [params] = useSearchParams();
  const submitting = useRef(false);

  const [formData, setFormData] = useState<HoldFormData>(() => {
    const start = params.get('startDate');
    const end = params.get('endDate');
    return {
      ...initialFormData,
      title: params.get('title') || '',
      stage: params.get('stage') || '',
      notes: params.get('notes') || '',
      holdDate: start ? parseDateOnlyToLocalDate(start) : null,
      endDate: end ? parseDateOnlyToLocalDate(end) : null,
      isMultiDay: Boolean(start && end && start !== end),
    };
  });
  const [stages, setStages] = useState<Stage[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [result, setResult] = useState<HoldMultiDayResult | null>(null);
  const [existingPromoterTeam, setExistingPromoterTeam] = useState<number | null>(null);

  const isEditing = Boolean(id);

  // Load stages for selection
  useEffect(() => {
    const loadStages = async () => {
      try {
        const response = await stagesApi.stagesList();
        const availableStages = response.results || [];
        setStages(availableStages);
        if (!isEditing) {
          setFormData((prev) => ({
            ...prev,
            stage: getSelectedOptionId(availableStages, prev.stage),
          }));
        }
      } catch {
        setError("Failed to load stages");
      }
    };

    loadStages();
  }, [stagesApi, isEditing]);

  // Load existing hold for editing
  useEffect(() => {
    if (isEditing && id) {
      const loadHold = async () => {
        setLoading(true);
        try {
          const hold = await holdsApi.holdsRetrieve({ id });
          setExistingPromoterTeam(hold.promoterTeam);
          setFormData({
            title: hold.showTitle || "",
            stage: hold.stage,
            holdDate: parseDateOnlyToLocalDate(apiDateToDateOnly(hold.holdDate)),
            endDate: null, // Single day hold
            isMultiDay: false,
            notes: hold.notes || "",

            // Operational fields - Timing
            doorsTime: hold.doorsTime
              ? new Date(`2000-01-01T${hold.doorsTime}`)
              : null,
            showTime: hold.showTime
              ? new Date(`2000-01-01T${hold.showTime}`)
              : null,
            endTime: hold.endTime
              ? new Date(`2000-01-01T${hold.endTime}`)
              : null,

            // Operational fields - Attendance
            attendance: hold.attendance || null,
            showCapacity: hold.showCapacity || null,

            // Operational fields - Revenue
            ticketRevenue: hold.ticketRevenue || "",
            barRevenue: hold.barRevenue || "",
            otherRevenue: hold.otherRevenue || "",

            // Operational fields - Expenses & Settlement
            totalExpenses: hold.totalExpenses || "",
            isSettled: hold.isSettled || false,
            settlementDate: hold.settlementDate
              ? parseDateOnlyToLocalDate(apiDateToDateOnly(hold.settlementDate))
              : null,
            settlementNotes: hold.settlementNotes || "",
          });
        } catch {
          setError("Failed to load hold");
        } finally {
          setLoading(false);
        }
      };

      loadHold();
    }
  }, [id, isEditing, holdsApi]);

  const handleInputChange = (
    field: keyof HoldFormData,
    value: string | Date | boolean | number | null,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
    setSuccess(null);
  };

  const validateForm = (): string | null => {
    if (!isEditing && !formData.title.trim()) {
      return "Show title is required";
    }
    if (!formData.stage) {
      return "Stage selection is required";
    }
    if (!formData.holdDate || Number.isNaN(formData.holdDate.getTime())) {
      return "A valid hold date is required";
    }
    if (formData.settlementDate && Number.isNaN(formData.settlementDate.getTime())) {
      return 'Enter a valid settlement date';
    }
    if (formData.isMultiDay && (!formData.endDate || Number.isNaN(formData.endDate.getTime()))) {
      return "End date is required for multi-day holds";
    }
    if (
      formData.isMultiDay &&
      formData.endDate &&
      formData.holdDate &&
      formData.endDate < formData.holdDate
    ) {
      return "End date must be after start date";
    }
    if (!user?.id) {
      return "User not authenticated";
    }

    return null;
  };

  const handleSubmit = async () => {
    if (submitting.current || (result && result.createdCount > 0)) return;
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    submitting.current = true;
    setSaving(true);
    setError(null);
    setSuccess(null);
    setResult(null);

    try {
      const promoterTeam = isEditing ? { id: existingPromoterTeam } : team;
      if (!promoterTeam?.id) {
        setError('Select a team before creating holds.');
        return;
      }
      if (formData.isMultiDay && formData.holdDate && formData.endDate) {
        const saved = await holdsApi.holdsCreateMultiDay({
          holdsCreateMultiDayRequest: {
            title: formData.title.trim(), stage: formData.stage,
            startDate: createDateUTC(formData.holdDate), endDate: createDateUTC(formData.endDate),
            promoterTeam: promoterTeam.id, notes: formData.notes,
          },
        });
        setResult(saved);
      } else {
        // Single-day hold creation
        const holdData: Omit<HoldCreateUpdate, "id" | "createdBy" | "canManage" | "hostTeam" | "showTeam" | "bookingRevision" | "acceptedAt"> = {
          stage: formData.stage,
          holdDate: createDateUTC(formData.holdDate!),
          promoterTeam: promoterTeam.id,
          notes: formData.notes,
          ...(!isEditing && { title: formData.title.trim() }),

          // Operational fields - Timing
          doorsTime: formData.doorsTime
            ? formData.doorsTime.toTimeString().slice(0, 5)
            : undefined,
          showTime: formData.showTime
            ? formData.showTime.toTimeString().slice(0, 5)
            : undefined,
          endTime: formData.endTime
            ? formData.endTime.toTimeString().slice(0, 5)
            : undefined,

          // Operational fields - Attendance
          attendance: formData.attendance || undefined,
          showCapacity: formData.showCapacity || undefined,

          // Operational fields - Revenue
          ticketRevenue: formData.ticketRevenue || undefined,
          barRevenue: formData.barRevenue || undefined,
          otherRevenue: formData.otherRevenue || undefined,

          // Operational fields - Expenses & Settlement
          totalExpenses: formData.totalExpenses || undefined,
          isSettled: formData.isSettled,
          settlementDate: formData.settlementDate ? createDateUTC(formData.settlementDate) : undefined,
          settlementNotes: formData.settlementNotes || undefined,
        };

        if (isEditing && id) {
          await holdsApi.holdsUpdate({
            id,
            holdCreateUpdate: holdData as HoldCreateUpdate,
          });
          setSuccess("Hold updated successfully!");
        } else {
          await holdsApi.holdsCreate({
            holdCreateUpdate: holdData as HoldCreateUpdate,
          });
          setSuccess("Hold created successfully!");
          // Navigate to the new hold details or back to calendar
          setTimeout(() => {
            navigate(`/shows/calendar`);
          }, 1500);
        }
      }
    } catch (err: unknown) {
      const rejected = await readHoldCreationError(err);
      setResult(rejected.result);
      if (!rejected.result) setError(rejected.message);
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1); // Go back to previous page
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  const breadcrumbItems = [
    { label: "Shows", path: "/shows" },
    { label: "Calendar", path: "/shows/calendar" },
    { label: isEditing ? "Edit Hold" : "New Hold" },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 0, sm: 3 }, overflowWrap: 'anywhere' }}>
        <Breadcrumbs items={breadcrumbItems} />

        <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
          {isEditing ? "Edit Hold" : "Create New Hold"}
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          gutterBottom
          sx={{ mb: 3 }}
        >
          {isEditing
            ? "Update hold details. Note: Some fields may be locked based on hold status."
            : "Create a new hold to express interest in booking dates. Positions are assigned when saved; holds on confirmed dates are kept on standby."}
        </Typography>

        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Box component="fieldset" disabled={saving || Boolean(result && result.createdCount > 0)}
                sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
              <Stack spacing={3}>
              {/* Basic Information */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>

                <Stack spacing={2}>
                  <TextField
                    label="Show Title"
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    required
                    fullWidth
                    disabled={isEditing}
                    placeholder="Enter the name of your show/event"
                    helperText={isEditing
                      ? 'Change the title from show details.'
                      : 'This will be displayed on the calendar and in communications'}
                  />

                  <TextField
                    label="Stage"
                    select
                    value={formData.stage}
                    onChange={(e) => handleInputChange("stage", e.target.value)}
                    required
                    fullWidth
                    disabled={isEditing} // Cannot change stage for existing holds
                    helperText={
                      isEditing
                        ? "Stage cannot be changed for existing holds"
                        : "Select the stage you want to book"
                    }
                  >
                    {stages.map((stage) => (
                      <MenuItem key={stage.id} value={stage.id}>
                        {stage.venueName} - {stage.name} (Capacity:{" "}
                        {stage.capacity?.toLocaleString()})
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </Box>

              <Divider />

              {/* Date Selection */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Date Selection
                </Typography>

                <Stack spacing={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isMultiDay}
                        onChange={(e) =>
                          handleInputChange("isMultiDay", e.target.checked)
                        }
                        disabled={isEditing} // Cannot change date type for existing holds
                      />
                    }
                    label="Multi-day hold (tour/residency)"
                    disabled={isEditing}
                  />

                  <DatePicker
                    label={formData.isMultiDay ? "Start Date" : "Hold Date"}
                    value={formData.holdDate}
                    onChange={(date) => handleInputChange("holdDate", date)}
                    disabled={isEditing} // Cannot change dates for existing holds
                    slotProps={{
                      textField: {
                        required: true,
                        fullWidth: true,
                        helperText: isEditing
                          ? "Date cannot be changed for existing holds"
                          : "Select the date you want to place a hold on",
                      },
                    }}
                  />

                  {formData.isMultiDay && (
                    <DatePicker
                      label="End Date"
                      value={formData.endDate}
                      onChange={(date) => handleInputChange("endDate", date)}
                      disabled={isEditing}
                      minDate={formData.holdDate || undefined}
                      slotProps={{
                        textField: {
                          required: formData.isMultiDay,
                          fullWidth: true,
                          helperText: isEditing
                            ? "Date cannot be changed for existing holds"
                            : "Select the last date of your multi-day hold",
                        },
                      }}
                    />
                  )}
                </Stack>
              </Box>

              <Divider />

              {/* Operational Data - Show only for confirmed holds or when editing */}
              {formData.isMultiDay && (
                <Alert severity="info">Edit timing, attendance and settlement details on each saved hold.</Alert>
              )}
              {!formData.isMultiDay && (isEditing || formData.holdDate) && (
                <>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Show Timing
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Set specific times for this date. Leave blank if not yet
                      determined.
                    </Typography>

                    <Stack spacing={2} direction={{ xs: "column", md: "row" }}>
                      <TimePicker
                        label="Doors Time"
                        value={formData.doorsTime}
                        onChange={(time) =>
                          handleInputChange("doorsTime", time)
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            helperText: "When doors open for attendees",
                          },
                        }}
                      />

                      <TimePicker
                        label="Show Time"
                        value={formData.showTime}
                        onChange={(time) => handleInputChange("showTime", time)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            helperText: "When the show actually starts",
                          },
                        }}
                      />

                      <TimePicker
                        label="End Time"
                        value={formData.endTime}
                        onChange={(time) => handleInputChange("endTime", time)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            helperText: "When the show is expected to end",
                          },
                        }}
                      />
                    </Stack>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Attendance & Capacity
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Track actual attendance for this specific date.
                    </Typography>

                    <Stack spacing={2} direction={{ xs: "column", md: "row" }}>
                      <TextField
                        label="Show Capacity"
                        type="number"
                        value={formData.showCapacity || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "showCapacity",
                            e.target.value ? parseInt(e.target.value) : null,
                          )
                        }
                        fullWidth
                        inputProps={{ min: 0 }}
                        helperText="Show capacity for this date (can exceed venue capacity)"
                      />

                      <TextField
                        label="Actual Attendance"
                        type="number"
                        value={formData.attendance || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "attendance",
                            e.target.value ? parseInt(e.target.value) : null,
                          )
                        }
                        fullWidth
                        inputProps={{ min: 0 }}
                        helperText="How many people actually attended"
                      />
                    </Stack>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Revenue & Expenses
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Financial data for this specific date. Enter amounts
                      without currency symbols.
                    </Typography>

                    <Stack spacing={2}>
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                      >
                        <TextField
                          label="Ticket Revenue"
                          type="number"
                          value={formData.ticketRevenue}
                          onChange={(e) =>
                            handleInputChange("ticketRevenue", e.target.value)
                          }
                          fullWidth
                          inputProps={{ step: "0.01", min: 0 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          }}
                          helperText="Revenue from ticket sales"
                        />

                        <TextField
                          label="Bar Revenue"
                          type="number"
                          value={formData.barRevenue}
                          onChange={(e) =>
                            handleInputChange("barRevenue", e.target.value)
                          }
                          fullWidth
                          inputProps={{ step: "0.01", min: 0 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          }}
                          helperText="Revenue from bar/concessions"
                        />

                        <TextField
                          label="Other Revenue"
                          type="number"
                          value={formData.otherRevenue}
                          onChange={(e) =>
                            handleInputChange("otherRevenue", e.target.value)
                          }
                          fullWidth
                          inputProps={{ step: "0.01", min: 0 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          }}
                          helperText="Merchandise, parking, etc."
                        />
                      </Stack>

                      <TextField
                        label="Total Expenses"
                        type="number"
                        value={formData.totalExpenses}
                        onChange={(e) =>
                          handleInputChange("totalExpenses", e.target.value)
                        }
                        fullWidth
                        inputProps={{ step: "0.01", min: 0 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                        helperText="Production, staffing, and other expenses for this date"
                      />
                    </Stack>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Settlement
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Track settlement status for this specific date.
                    </Typography>

                    <Stack spacing={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.isSettled}
                            onChange={(e) =>
                              handleInputChange("isSettled", e.target.checked)
                            }
                          />
                        }
                        label="This date has been settled"
                      />

                      {formData.isSettled && (
                        <>
                          <DatePicker
                            label="Settlement Date"
                            value={formData.settlementDate}
                            onChange={(date) =>
                              handleInputChange("settlementDate", date)
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                helperText:
                                  "Date when this show instance was settled",
                              },
                            }}
                          />

                          <TextField
                            label="Settlement Notes"
                            value={formData.settlementNotes}
                            onChange={(e) =>
                              handleInputChange(
                                "settlementNotes",
                                e.target.value,
                              )
                            }
                            multiline
                            rows={2}
                            fullWidth
                            placeholder="Notes about the settlement process..."
                            helperText="Any important notes about how this date was settled"
                          />
                        </>
                      )}
                    </Stack>
                  </Box>
                </>
              )}

              <Divider />

              {/* Additional Information */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Additional Information
                </Typography>

                <TextField
                  label="Notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="Add any notes about this hold request..."
                  helperText="Private notes visible only to your promoting team"
                />
              </Box>

              </Stack>
              </Box>
              {result && <HoldCreationResult result={result} />}
              {/* Error/Success Messages */}
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {success}
                </Alert>
              )}

              {/* Action Buttons */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={saving || Boolean(result && result.createdCount > 0)}
                  size="large"
                >
                  {saving ? (
                    <>
                      <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                      {isEditing ? "Updating..." : "Creating..."}
                    </>
                  ) : isEditing ? (
                    "Update Hold"
                  ) : (
                    "Create Hold"
                  )}
                </Button>

                {result?.show && result.createdCount > 0 && (
                  <Button variant="outlined" onClick={() => navigate(`/shows/${result.show!.id}`)}>View Show</Button>
                )}
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={saving}
                  size="large"
                >
                  {result && result.createdCount > 0 ? 'Close' : 'Cancel'}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default HoldForm;
